'use client';

import React, { useEffect, useRef, useState } from 'react';

import { ModalPrinterComparisonVisId } from '@monetization/hpaip-components-library';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import useGetProductsData from '@storefront/hooks/useGetProductsData';
import DataCollection from '@storefront/services/DataCollection';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';

import {
  getInitialSelection,
  getResponsiveColumnCount,
  getShouldRenderSelect,
  handleFullSpecsAnalytics,
} from './modal-printer-comparison-utils';
import styles from './modal-printer-comparison.module.scss';
import {
  featuresKeys,
  printersFeaturesList,
} from './modal-printer-comparison.features';

interface ModalPrinterComparisonProps {
  noFirstText?: boolean;
  secondText?: string;
  underlineLink?: boolean;
}

export function ModalPrinterComparison({
  noFirstText,
  secondText,
  underlineLink,
}: Readonly<ModalPrinterComparisonProps>) {
  const { productsOptions, productsInfo, multipleProduct } =
    useGetProductsData();

  const productConfig = sessionStorage.getItem('product-configuration');
  const config = productConfig ? JSON.parse(productConfig) : undefined;
  const activeSKU = config?.activeSku || (null as unknown as string);

  const buttonRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();

  const [showModal, setShowModal] = useState(false);

  const productsOptionsLength = productsOptions ? productsOptions.length : 0;
  const responsiveColumnCount = getResponsiveColumnCount(productsOptionsLength);
  const [columnsNumber, setColumnsNumber] = useState(responsiveColumnCount);
  const initialSelection = getInitialSelection(
    activeSKU,
    productsOptions,
    columnsNumber
  );

  const featuresList = printersFeaturesList(multipleProduct);

  useEffect(() => {
    const resizeHandler = () => {
      const currentResponsiveColumnCount = getResponsiveColumnCount(
        productsOptionsLength
      );
      setColumnsNumber(currentResponsiveColumnCount);
    };
    resizeHandler();
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, []);

  useEffect(() => {
    if (showModal) {
      DataCollection.sendEvent({
        ...uiEvents.comparePrintersModalDisplayed,
      });
    }
  }, [showModal]);

  useEffect(() => {
    const handlePopState = () => setShowModal(false);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const shouldRenderSelect = getShouldRenderSelect(
    columnsNumber,
    productsOptions
  );
  if (!productsOptions || productsOptions.length === 0) return null;

  return (
    <>
      <div className={styles['comparisonModal']}>
        <ModalPrinterComparisonVisId
          isOpen={showModal}
          initialSelection={initialSelection}
          productsOptions={productsOptions}
          labels={{
            title: shouldRenderSelect ? t('modals.modalComparison.title') : '',
            fullSpecs: t('modals.modalComparison.specs'),
          }}
          productsInfo={productsInfo}
          featuresData={{
            featuresKeys: featuresKeys,
            printersFeaturesList: featuresList,
          }}
          setOpenModal={setShowModal}
          onAnalytics={handleFullSpecsAnalytics}
        />
      </div>
      <div className={styles['modalAction']}>
        {!noFirstText && (
          <span className={classNames(styles['modalText'], 'caption-regular')}>
            {t('modals.modalComparison.decide')}
          </span>
        )}
        <input
          id="open-printer-modal-printer-comparison"
          ref={buttonRef}
          tabIndex={0}
          data-testid="showModalButton"
          type="button"
          className={classNames(
            styles['modal-button'],
            underlineLink && styles['underlinedLink'],
            'caption-regular'
          )}
          value={secondText || t('modals.modalComparison.modalName')}
          onClick={async () => {
            await DataCollection.sendEvent({
              ...uiEvents.printerConfigViewMore,
              screenMode: 'ChooseYourPrinter',
              controlName: 'ComparePrinters',
            });
            setShowModal(true);
          }}
        />
      </div>
    </>
  );
}

export default ModalPrinterComparison;
